// Copyright 2026 Hardfin, Inc.
import {
  Download as DownloadIcon,
  OpenInNewRounded,
} from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface TemplateDownloadProps {
  orgTimezone: string;
  onBack: () => void;
  onNext: () => void;
}

export function TemplateDownload(props: TemplateDownloadProps) {
  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Asset serial',
      'Origin',
      'Origin zone',
      'Expected ship date',
      'Shipped at',
      `Shipped at timezone (Ex: ${props.orgTimezone})`,
      'Destination',
      'Destination zone',
      'Expected deliver date',
      'Delivered at',
      `Delivered at timezone (Ex: ${props.orgTimezone})`,
      'Carrier',
      'Tracking number',
    ];

    const exampleRow = [
      'SN-001',
      'Warehouse A',
      '',
      '2025-01-15',
      '2025-01-15 09:00',
      props.orgTimezone,
      'Customer Site B',
      '',
      '2025-01-20',
      '2025-01-20 14:00',
      props.orgTimezone,
      'FedEx',
      '1234567890',
    ];

    const csvContent = headers.join(',') + '\n' + exampleRow.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `move_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [props.orgTimezone]);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Template for importing moves
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}>
            <li>
              <Typography variant="body2">Asset serial - text</Typography>
            </li>
            <li>
              <Typography variant="body2">Origin - location name</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Destination - location name
              </Typography>
            </li>
          </ul>
          <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={4}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Origin / ship fields
              </Typography>
              <ul
                style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}
              >
                <li>
                  <Typography variant="body2">
                    Origin zone - zone name
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Expected ship date - date (YYYY-MM-DD)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Shipped at - datetime (YYYY-MM-DD HH:mm)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Shipped at timezone - IANA timezone
                  </Typography>
                </li>
              </ul>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Destination / deliver fields
              </Typography>
              <ul
                style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}
              >
                <li>
                  <Typography variant="body2">
                    Destination zone - zone name
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Expected deliver date - date (YYYY-MM-DD)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Delivered at - datetime (YYYY-MM-DD HH:mm)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Delivered at timezone - IANA timezone
                  </Typography>
                </li>
              </ul>
            </Box>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Tracking information
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">Carrier - carrier name</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Tracking number - tracking identifier
              </Typography>
            </li>
          </ul>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Timezone columns use IANA timezone names (e.g., America/Chicago,
              Europe/London).{' '}
              <Link
                href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                View full list
                <OpenInNewRounded sx={{ fontSize: 14 }} />
              </Link>
            </li>
            <li>
              US abbreviations are supported: EST, EDT, CST, CDT, PST, PDT, MST,
              MDT
            </li>
            <li>Timezone is required when a timestamp is provided</li>
            <li>
              Your organization timezone ({props.orgTimezone}) is used as the
              default in the template
            </li>
            <li>
              Date format must be YYYY-MM-DD (e.g., 2025-01-15 for January 15,
              2025)
            </li>
            <li>
              Datetime format must be YYYY-MM-DD HH:mm (e.g., 2025-01-15 09:00)
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
